import { FormEvent, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import { useApiClient } from '../../lib/apiClient';

type AuditSource = 'all' | 'platform' | 'tenant' | 'agent';

type AuditItem = {
  id: string;
  source: 'platform' | 'tenant' | 'agent';
  createdAt: string;
  action: string;
  entity: string;
  entityId: string;
  organizationId: string | null;
  organizationName: string | null;
  actorEmail: string | null;
  actorName: string | null;
  targetDisplay: string | null;
  metadata: Record<string, unknown> | null;
};

type AuditFeedPayload = {
  data: AuditItem[];
  meta: {
    limit: number;
    returned: number;
    counts: {
      platform: number;
      tenant: number;
      agent: number;
    };
  };
};

type OrganizationOption = {
  id: string;
  name: string;
};

const inputClass = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-400';

function formatDateTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'n/a';
  return date.toLocaleString();
}

function sourcePill(source: AuditItem['source']) {
  if (source === 'platform') return 'pill pill-info';
  if (source === 'agent') return 'pill pill-warn';
  return 'pill pill-success';
}

function sourceLabel(source: AuditItem['source']) {
  if (source === 'platform') return 'Platform';
  if (source === 'agent') return 'Agent';
  return 'Tenant';
}

export function AuditLogPage() {
  const { fetchJson } = useApiClient();
  const [rows, setRows] = useState<AuditItem[]>([]);
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<AuditFeedPayload['meta'] | null>(null);
  const [filters, setFilters] = useState({
    source: 'all' as AuditSource,
    organizationId: '',
    actorEmail: '',
    action: '',
    entity: '',
    limit: '100',
  });

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set('source', filters.source);
    params.set('limit', filters.limit || '100');
    if (filters.organizationId) params.set('organizationId', filters.organizationId);
    if (filters.actorEmail) params.set('actorEmail', filters.actorEmail);
    if (filters.action) params.set('action', filters.action);
    if (filters.entity) params.set('entity', filters.entity);
    return params.toString();
  }, [filters]);

  const load = () => {
    setLoading(true);
    fetchJson<AuditFeedPayload>(`/platform-admin/audit?${queryString}`)
      .then((payload) => {
        setRows(payload.data);
        setMeta(payload.meta);
        setError(null);
      })
      .catch((err) => {
        setError(String(err?.message || 'Unable to load audit log.'));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchJson<{ data: Array<{ id: string; name: string }> }>('/platform-admin/organizations')
      .then((payload) => setOrganizations(payload.data.map((item) => ({ id: item.id, name: item.name }))))
      .catch(() => setOrganizations([]));
  }, [fetchJson]);

  useEffect(() => {
    load();
  }, [queryString]);

  const submitFilters = (event: FormEvent) => {
    event.preventDefault();
    load();
  };

  return (
    <div className="space-y-6">
      <div className="card p-5 lg:p-6">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Global Audit Log</h2>
            <p className="mt-1 text-sm text-slate-500">Merged activity feed for platform admin actions, tenant audit events, and AI agent audit events.</p>
          </div>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            onClick={load}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        <form className="grid gap-4 md:grid-cols-2 xl:grid-cols-6" onSubmit={submitFilters}>
          <label className="block text-sm">
            <span className="mb-2 block font-medium text-slate-600">Source</span>
            <select className={inputClass} value={filters.source} onChange={(event) => setFilters({ ...filters, source: event.target.value as AuditSource })}>
              <option value="all">All</option>
              <option value="platform">Platform</option>
              <option value="tenant">Tenant</option>
              <option value="agent">Agent</option>
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-2 block font-medium text-slate-600">Organization</span>
            <select className={inputClass} value={filters.organizationId} onChange={(event) => setFilters({ ...filters, organizationId: event.target.value })}>
              <option value="">All organizations</option>
              {organizations.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-2 block font-medium text-slate-600">Actor email</span>
            <input className={inputClass} value={filters.actorEmail} onChange={(event) => setFilters({ ...filters, actorEmail: event.target.value })} placeholder="ceo@corzosoft.com" />
          </label>
          <label className="block text-sm">
            <span className="mb-2 block font-medium text-slate-600">Action</span>
            <input className={inputClass} value={filters.action} onChange={(event) => setFilters({ ...filters, action: event.target.value })} placeholder="tenant.update" />
          </label>
          <label className="block text-sm">
            <span className="mb-2 block font-medium text-slate-600">Entity</span>
            <input className={inputClass} value={filters.entity} onChange={(event) => setFilters({ ...filters, entity: event.target.value })} placeholder="organization_user" />
          </label>
          <label className="block text-sm">
            <span className="mb-2 block font-medium text-slate-600">Limit</span>
            <input className={inputClass} type="number" min={1} max={200} value={filters.limit} onChange={(event) => setFilters({ ...filters, limit: event.target.value })} />
          </label>
          <div className="md:col-span-2 xl:col-span-6 flex justify-end">
            <button type="submit" className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white">
              <Search size={16} />
              Apply filters
            </button>
          </div>
        </form>
      </div>

      {meta ? (
        <div className="grid gap-4 md:grid-cols-4">
          <MetricCard label="Returned" value={String(meta.returned)} />
          <MetricCard label="Platform" value={String(meta.counts.platform)} />
          <MetricCard label="Tenant" value={String(meta.counts.tenant)} />
          <MetricCard label="Agent" value={String(meta.counts.agent)} />
        </div>
      ) : null}

      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-5 text-sm text-slate-500">Loading audit log...</div>
        ) : error ? (
          <div className="p-5 text-sm text-rose-600">{error}</div>
        ) : !rows.length ? (
          <div className="p-5 text-sm text-slate-500">No audit events matched the current filters.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map((item) => (
              <div key={`${item.source}-${item.id}`} className="p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={sourcePill(item.source)}>{sourceLabel(item.source)}</span>
                      <span className="text-sm font-semibold text-slate-900">{item.action}</span>
                      <span className="text-xs text-slate-400">{formatDateTime(item.createdAt)}</span>
                    </div>
                    <div className="text-sm text-slate-600">
                      <span className="font-medium text-slate-900">{item.entity}</span>
                      <span className="mx-2 text-slate-300">•</span>
                      <span>{item.targetDisplay || item.entityId}</span>
                    </div>
                    <div className="text-sm text-slate-500">
                      {item.organizationName || 'Global'} {item.actorEmail ? `• ${item.actorName || item.actorEmail}` : '• system/public'}
                    </div>
                  </div>
                </div>
                {item.metadata && Object.keys(item.metadata).length ? (
                  <details className="mt-3">
                    <summary className="cursor-pointer text-sm font-medium text-slate-700">Metadata</summary>
                    <pre className="mt-3 overflow-x-auto rounded-2xl border border-slate-100 bg-slate-50 p-4 text-xs text-slate-700">
                      {JSON.stringify(item.metadata, null, 2)}
                    </pre>
                  </details>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="card p-5">
      <div className="text-sm text-slate-500">{label}</div>
      <div className="mt-2 text-3xl font-semibold text-slate-900">{value}</div>
    </div>
  );
}
